import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Star, ChevronRight, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function PricingPage() {
  const { user, token } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    axios.get(`${API_URL}/api/plans`).then(res => setPlans(res.data));
  }, []);

  const handleSubscribe = async (planId) => {
    if (!user) { navigate('/login'); return; }

    setLoading(planId);
    try {
      const res = await axios.post(`${API_URL}/api/checkout`, {
        plan: planId,
        origin_url: window.location.origin
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      window.location.href = res.data.url;
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen hero-gradient pt-24 pb-12">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold font-heading text-white mb-4">
            {t('pricing_title')} <span className="gradient-text">{t('pricing_plan')}</span>
          </h1>
          <p className="text-lg text-slate-400">{t('pricing_subtitle')}</p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {plans.map((plan, idx) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
              className={`glass rounded-2xl p-6 relative card-hover ${plan.popular ? 'ring-2 ring-emerald-500' : ''}`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-gradient-to-r from-emerald-500 to-blue-600 text-white text-xs font-medium flex items-center gap-1">
                  <Star className="w-3 h-3" /> {t('pricing_popular')}
                </div>
              )}

              <h3 className="text-xl font-bold text-white mb-2">{plan.name}</h3>
              <div className="flex items-baseline gap-1 mb-2">
                <span className="text-4xl font-bold text-white">${plan.price}</span>
                <span className="text-slate-400">{t('pricing_month')}</span>
              </div>

              <div className="flex items-center gap-4 mb-6 text-sm">
                <span className="text-emerald-400 font-medium">{plan.credits} {t('credits')}</span>
                <span className="text-blue-400 font-medium">{plan.max_llms} LLMs {t('max')}</span>
              </div>

              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, fidx) => (
                  <li key={fidx} className="flex items-center gap-3 text-slate-300">
                    <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleSubscribe(plan.id)}
                disabled={loading === plan.id || user?.plan === plan.id}
                className={`w-full py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 ${
                  plan.popular
                    ? 'bg-gradient-to-r from-emerald-500 to-blue-600 text-white hover:opacity-90'
                    : 'bg-white/5 border border-white/10 text-white hover:bg-white/10'
                } disabled:opacity-50`}
              >
                {loading === plan.id && <Loader2 className="w-5 h-5 animate-spin" />}
                {user?.plan === plan.id ? t('pricing_current') : t('pricing_choose')}
                <ChevronRight className="w-4 h-4" />
              </button>
            </motion.div>
          ))}
        </div>

        <div className="mt-12 glass rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">{t('pricing_cost_title')}</h3>
          <div className="grid md:grid-cols-4 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-emerald-400">1</p>
              <p className="text-slate-400 text-sm">{t('credit')} / 1 LLM</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-emerald-400">2</p>
              <p className="text-slate-400 text-sm">{t('credits')} / 2 LLMs</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-emerald-400">3</p>
              <p className="text-slate-400 text-sm">{t('credits')} / 3 LLMs</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-amber-400">+5</p>
              <p className="text-slate-400 text-sm">{t('credits')} / Deepfake</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
